const EMAIL_ENDPOINT =
  "https://zgr40f2dtg.execute-api.us-east-1.amazonaws.com/prod/enviar-email";

// Keep payload conservative to avoid API Gateway/Lambda 413.
const MAX_REQUEST_BYTES = 900 * 1024;

const sanitizeBase64 = (value) => {
  // Normaliza base64 removendo espacos e prefixo data URI quando existir.
  const raw = String(value || "").trim();
  if (!raw) return "";

  // Supports full data URI and plain base64.
  const commaIndex = raw.indexOf(",");
  const base64Part =
    raw.startsWith("data:") && commaIndex >= 0 ? raw.slice(commaIndex + 1) : raw;

  return base64Part.replace(/\s+/g, "");
};

const getByteLength = (text) => {
  // Mede tamanho real em bytes do payload JSON antes do POST.
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(text).length;
  }
  return new Blob([text]).size;
};

const bytesToBase64 = (bytesLike) => {
  // Converte bytes/ArrayBuffer para base64 sem depender de bibliotecas externas.
  const bytes =
    bytesLike instanceof Uint8Array
      ? bytesLike
      : bytesLike instanceof ArrayBuffer
        ? new Uint8Array(bytesLike)
        : null;

  if (!bytes) return "";

  let binary = "";
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
};

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    // Converte Blob/File para base64 para permitir anexos vindos de input de arquivo.
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result || "");
      resolve(sanitizeBase64(result));
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const normalizeAttachment = async (attachment, index) => {
  // Aceita multiplos formatos de anexo e sempre retorna no padrao esperado pelo backend.
  if (!attachment) return null;

  const filename =
    attachment.filename ||
    attachment.fileName ||
    attachment.name ||
    `anexo-${index + 1}.pdf`;

  const fallbackContentType = attachment.contentType || "application/pdf";

  if (typeof attachment.content === "string" && attachment.content.trim()) {
    // Cenario mais comum: conteudo ja veio em base64.
    return {
      filename,
      contentType: fallbackContentType,
      content: sanitizeBase64(attachment.content),
      encoding: "base64",
      disposition: "attachment",
    };
  }

  if (attachment.file instanceof Blob) {
    // Converte arquivo enviado pelo navegador para base64.
    const content = await blobToBase64(attachment.file);
    return {
      filename,
      contentType: attachment.file.type || fallbackContentType,
      content,
      encoding: "base64",
      disposition: "attachment",
    };
  }

  if (
    attachment.bytes instanceof Uint8Array ||
    attachment.bytes instanceof ArrayBuffer
  ) {
    // Converte binario puro para base64.
    return {
      filename,
      contentType: fallbackContentType,
      content: bytesToBase64(attachment.bytes),
      encoding: "base64",
      disposition: "attachment",
    };
  }

  if (typeof attachment.url === "string" && attachment.url.trim()) {
    // Legacy fallback: baixa arquivo remoto e converte para base64.
    const response = await fetch(attachment.url.trim());
    if (!response.ok) {
      throw new Error(`Falha ao baixar anexo: ${attachment.url}`);
    }
    const blob = await response.blob();
    return {
      filename,
      contentType: blob.type || fallbackContentType,
      content: await blobToBase64(blob),
      encoding: "base64",
      disposition: "attachment",
    };
  }

  return null;
};

const normalizeAttachmentReference = (attachment, index) => {
  // Envia apenas referencia (url/path/id) para o backend montar o anexo.
  if (!attachment) return null;

  const filename =
    attachment.filename ||
    attachment.fileName ||
    attachment.name ||
    `anexo-${index + 1}.pdf`;

  const fallbackContentType = attachment.contentType || "application/pdf";
  const url =
    typeof attachment.url === "string" && attachment.url.trim()
      ? attachment.url.trim()
      : "";
  const storagePath =
    typeof attachment.storagePath === "string" && attachment.storagePath.trim()
      ? attachment.storagePath.trim()
      : typeof attachment.path === "string" && attachment.path.trim()
        ? attachment.path.trim()
        : typeof attachment.fileId === "string" && attachment.fileId.trim()
          ? attachment.fileId.trim()
          : "";

  if (!url && !storagePath) return null;

  return {
    filename,
    contentType: fallbackContentType,
    disposition: "attachment",
    source: "remote",
    ...(url ? { url } : {}),
    ...(storagePath ? { storagePath } : {}),
  };
};

const parseResponseBody = async (response) => {
  // Faz parse seguro de JSON e tolera respostas HTML/texto (ex.: erro 413).
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
};

const sendEmailBudget = async ({
  to,
  subject,
  html,
  replyTo,
  attachments = [],
  attachmentRefs = [],
}) => {
  try {
    // Normaliza anexos em base64 (modo legado) em paralelo.
    const normalizedAttachments = await Promise.all(
      (Array.isArray(attachments) ? attachments : []).map((attachment, index) =>
        normalizeAttachment(attachment, index),
      ),
    );

    // Normaliza referencias de arquivo (modo recomendado para evitar payload grande).
    const normalizedAttachmentRefs =
      (Array.isArray(attachmentRefs) ? attachmentRefs : [])
        .map((attachment, index) => normalizeAttachmentReference(attachment, index))
        .filter(Boolean);

    const validAttachments = normalizedAttachments.filter(Boolean);

    // Monta payload minimo e inclui campos opcionais apenas quando informados.
    const payload = { to, subject, html };
    if (replyTo) payload.replyTo = replyTo;
    if (validAttachments.length > 0) payload.attachments = validAttachments;
    if (normalizedAttachmentRefs.length > 0) {
      payload.attachmentRefs = normalizedAttachmentRefs;
    }

    const body = JSON.stringify(payload);
    const bodyBytes = getByteLength(body);

    // Bloqueio local para evitar requisicao que ja sabemos que vai estourar no endpoint.
    if (bodyBytes > MAX_REQUEST_BYTES) {
      return {
        success: false,
        error: {
          code: "PAYLOAD_TOO_LARGE_LOCAL",
          message:
            "Payload muito grande. Envie URL/path do PDF para anexo server-side ou apenas o link.",
          maxBytes: MAX_REQUEST_BYTES,
          sizeBytes: bodyBytes,
        },
      };
    }

    const response = await fetch(EMAIL_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body,
    });

    const result = await parseResponseBody(response);

    // Trata erro HTTP sem quebrar quando a API responder HTML/texto.
    if (!response.ok) {
      const message =
        response.status === 413
          ? "Payload muito grande para o endpoint (HTTP 413). Envie URL/path do PDF para anexo server-side ou use link."
          : "Erro ao enviar e-mail.";

      console.error(message, result);
      return {
        success: false,
        error: {
          status: response.status,
          message,
          details: result,
        },
      };
    }

    return { success: true, data: result };
  } catch (error) {
    // Erros de rede, parse ou normalizacao de anexo caem aqui.
    console.error("Erro na requisicao de e-mail:", error);
    return { success: false, error };
  }
};

const emailServiceBudget = {
  sendEmailBudget,
  sendEmail: sendEmailBudget,
};

export default emailServiceBudget;
